/* eslint-disable no-use-before-define */
// @flow
import React, { useState, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import Icon from 'components/_particles/icon/react/Icon';
import Button from 'components/atoms/button/Button';
import type { NavigationItem } from '../types';
import ItemGrid from './ItemGrid';

export type Props = {
  items?: NavigationItem[],
};

const Mainmenu = ({ items }: Props): React$MixedElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<NavigationItem | null>(null);
  const [accessibleMode, setAccessibleMode] = useState(false);
  const trigger = useRef<HTMLElement | null>(null);
  const scrollPositionOnOpen = useRef(0);
  const closeTimeout = 250;

  // Close every other overlay that listens to the overlayClose event on document
  const throwOverlayCloseEvent = () => {
    const overlayCloseEvent = new CustomEvent('overlayClose', {
      bubbles: true,
      detail: { target: trigger.current },
    });

    document.dispatchEvent(overlayCloseEvent);
  };

  const handleScroll = () => {
    if (
      window.scrollY - scrollPositionOnOpen.current > 300 ||
      window.scrollY - scrollPositionOnOpen.current < -300
    ) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    window.removeEventListener('scroll', handleScroll);
    setTimeout(() => {
      setActiveItem(null);
    }, closeTimeout);
  };

  const handleOpen = (item: NavigationItem) => {
    throwOverlayCloseEvent();
    setActiveItem(item);
    setIsOpen(true);
    scrollPositionOnOpen.current = window.scrollY;
    window.addEventListener('scroll', handleScroll);
  };

  const toggle = (open: boolean, item?: NavigationItem) => {
    if (open && item?.title !== activeItem?.title && item) {
      handleOpen(item);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    document.addEventListener('overlayClose', (e: any) => {
      if (e.detail?.target !== trigger.current) {
        handleClose();
      }
    });
  }, []);

  useEffect(() => {
    // Detect keyboard navigation
    const handleKeyDown = (event: any) => {
      if (event.key === 'Tab') {
        setAccessibleMode(true);
        window.removeEventListener('keydown', handleKeyDown);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <ul
        className={classNames(
          'header__mainnav-list main-menu',
          isOpen && 'main-menu--open'
        )}
        ref={trigger}
      >
        {items?.length &&
          items?.map((item, index) => (
            <li
              key={index}
              className={classNames(
                'header__item',
                item.active && 'header__item--active',
                item.current && 'header__item--current',
                item.title === activeItem?.title && 'header__item--open'
              )}
            >
              {item.hasSubpages ? (
                <button
                  type="button"
                  onClick={() => toggle(true, item)}
                  className="header__link"
                >
                  <span className="header__text">{item.title}</span>
                </button>
              ) : (
                <a href={item.link} className="header__link">
                  <span className="header__text">{item.title}</span>
                </a>
              )}
            </li>
          ))}
      </ul>

      <ReactModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        contentLabel={t('Menü aufgeklappt')}
        overlayClassName="main-menu__overlay"
        className="main-menu__flyout-wrapper"
        shouldReturnFocusAfterClose
        closeTimeoutMS={closeTimeout}
      >
        <div className="main-menu__flyout">
          {accessibleMode && (
            <div className="main-menu__close">
              <Button
                style="icon"
                icon="close"
                iconTitle={t('Menü Schließen')}
                onClick={handleClose}
                className="button--secondary"
              />
            </div>
          )}
          <div
            className={classNames(
              'header__container',
              accessibleMode && 'header__container--close'
            )}
          >
            <ItemGrid
              item={activeItem}
              modifier={activeItem?.title?.replace(/\s/g, '') || ''}
            />
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default Mainmenu;
